import {
  ATTACHMENT_ACCEPT,
  isImageType,
  MAX_ATTACHMENTS_PER_MESSAGE,
  type Attachment,
  type ChatMessage,
  type RequestContextValues,
  type RunErrorCode,
  type RunUsage,
} from '@agent-platform/shared';
import { Link } from '@tanstack/react-router';
import {
  ArrowUp,
  Check,
  CircleAlert,
  Layers,
  LoaderCircle,
  Paperclip,
  RotateCcw,
  Square,
  Upload,
  Wrench,
} from 'lucide-react';
import {
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import {
  MessageAttachments,
  PendingAttachments,
  type PendingAttachment,
} from '@/components/attachments';
import { Button } from '@/components/ui/button';
import { Markdown } from '@/components/markdown';
import { errorMessage } from '@/lib/api';
import { randomId } from '@/lib/browser';
import { toAttachment, uploadFile } from '@/lib/files';
import { usePreferences } from '@/lib/preferences';
import { postEventStream } from '@/lib/sse';
import { cn } from '@/lib/utils';

interface Turn {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  /** User turns only. */
  attachments?: Attachment[];
  /** Assistant turns only. */
  run?: {
    runId: string | null;
    version: number | null;
    status: 'streaming' | 'succeeded' | 'failed';
    latencyMs?: number;
    usage?: RunUsage;
    error?: { code: RunErrorCode | 'request'; message: string };
    tools?: Array<{ id: string; name: string; args: unknown; ok?: boolean }>;
    /** Session levels the run used. */
    sessions?: Array<{ level: string; key: string; sessionId: string }>;
  };
}

/** Successful exchanges only: a failed reply (and the message it answered) is not resent. */
function history(turns: Turn[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  for (let i = 0; i < turns.length; i++) {
    const turn = turns[i]!;
    const reply = turns[i + 1];
    if (turn.role === 'user' && reply?.role === 'assistant' && reply.run?.status === 'succeeded') {
      out.push(
        {
          role: 'user',
          content: turn.content,
          ...(turn.attachments?.length ? { attachments: turn.attachments } : {}),
        },
        { role: 'assistant', content: reply.content },
      );
      i++;
    }
  }
  return out;
}

function ToolCalls({ tools }: { tools: NonNullable<NonNullable<Turn['run']>['tools']> }) {
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label="Tool calls">
      {tools.map((t) => (
        <li key={t.id} data-testid="tool-call">
          <details className="group bg-muted/70 open:bg-card open:shadow-panel rounded-lg text-xs open:w-full">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 px-2 py-1 font-mono">
              <Wrench className="text-muted-foreground size-3" aria-hidden />
              {t.name}
              {t.ok === undefined ? (
                <LoaderCircle className="size-3 animate-spin" aria-label="running" />
              ) : t.ok ? (
                <Check className="size-3 text-green-600" aria-label="succeeded" />
              ) : (
                <CircleAlert className="text-destructive size-3" aria-label="failed" />
              )}
            </summary>
            <pre className="text-muted-foreground max-h-48 overflow-auto px-2 pb-2 font-mono text-[11px] whitespace-pre-wrap">
              {JSON.stringify(t.args, null, 2)}
            </pre>
          </details>
        </li>
      ))}
    </ul>
  );
}

function RunMeta({ run }: { run: NonNullable<Turn['run']> }) {
  if (run.status === 'streaming') return null;
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {run.sessions && run.sessions.length > 0 && (
        <Link
          to="/sessions"
          className="hover:text-foreground inline-flex items-center gap-1"
          title="Session context this reply used (open Session context)"
          data-testid="session-levels"
        >
          <Layers className="size-3" aria-hidden />
          {run.sessions.map((s) => (s.key === '*' ? s.level : `${s.level}=${s.key}`)).join(' · ')}
        </Link>
      )}
      {run.version !== null && <span>v{run.version}</span>}
      {run.latencyMs !== undefined && <span>{(run.latencyMs / 1000).toFixed(1)} s</span>}
      {run.usage && (
        <span>
          {run.usage.inputTokens} in, {run.usage.outputTokens} out tokens
        </span>
      )}
      {run.runId && (
        <Link
          to="/traces/runs/$runId"
          params={{ runId: run.runId }}
          className="hover:text-foreground font-mono"
          aria-label="View run details"
        >
          run {run.runId.slice(-8)}
        </Link>
      )}
    </span>
  );
}

/**
 * Chat with an agent through the playground API: streams the reply, shows tool calls as they
 * happen, and records every reply as a run. Stateful agents keep the thread server-side.
 */
export function AgentChat({
  agentId,
  agentName,
  version,
  stateful,
  requestContext,
  headers,
  toolbar,
  className,
}: {
  agentId: string;
  agentName: string;
  /** `draft`, `published` or a version id. */
  version: string;
  /** The server keeps the conversation (thread memory or session levels): send only new turns. */
  stateful: boolean;
  /** `null` while the request context is invalid (sending is disabled). */
  requestContext: RequestContextValues | null;
  /** Test headers run through the agent's endpoint mapping (omit to skip the mapping). */
  headers?: Record<string, string>;
  /** Controls on the composer's bottom row (e.g. version and context pickers). */
  toolbar?: ReactNode;
  className?: string;
}) {
  const [turns, setTurns] = useState<Turn[]>([]);
  // Stateful agents remember the thread server-side; a new chat starts a new thread.
  const [threadId, setThreadId] = useState(() => randomId());
  const [input, setInput] = useState('');
  const [running, setRunning] = useState(false);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const uploads = useRef(new Map<string, AbortController>());
  const fileRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const nextId = useRef(1);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [turns]);
  useEffect(
    () => () => {
      abortRef.current?.abort();
      for (const c of uploads.current.values()) c.abort();
    },
    [],
  );
  // Local previews are released with their chips.
  const previews = useRef(new Set<string>());
  useEffect(() => {
    const current = new Set(
      pending.map((p) => p.previewUrl).filter((u): u is string => Boolean(u)),
    );
    for (const url of previews.current) if (!current.has(url)) URL.revokeObjectURL(url);
    previews.current = current;
  }, [pending]);
  useEffect(() => () => previews.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const patchPending = (localId: string, patch: Partial<PendingAttachment>) =>
    setPending((all) => all.map((p) => (p.localId === localId ? { ...p, ...patch } : p)));

  function addFiles(list: FileList | File[]) {
    const files = [...list];
    if (files.length === 0) return;
    const room = MAX_ATTACHMENTS_PER_MESSAGE - pending.length;
    if (room <= 0) {
      toast.error(`At most ${MAX_ATTACHMENTS_PER_MESSAGE} files per message`);
      return;
    }
    if (files.length > room)
      toast.warning(`Only the first ${room} file${room === 1 ? '' : 's'} were attached`);
    for (const file of files.slice(0, room)) {
      const localId = randomId();
      const controller = new AbortController();
      uploads.current.set(localId, controller);
      setPending((all) => [
        ...all,
        {
          localId,
          name: file.name || 'pasted image',
          sizeBytes: file.size,
          previewUrl: isImageType(file.type) ? URL.createObjectURL(file) : null,
          status: 'uploading',
          progress: 0,
        },
      ]);
      void uploadFile(file, (progress) => patchPending(localId, { progress }), controller.signal)
        .then((dto) =>
          patchPending(localId, {
            status: 'ready',
            attachment: toAttachment(dto),
            name: dto.name,
            sizeBytes: dto.sizeBytes,
          }),
        )
        .catch((error: unknown) => {
          if ((error as Error).name !== 'AbortError')
            patchPending(localId, { status: 'error', error: errorMessage(error) });
        })
        .finally(() => uploads.current.delete(localId));
    }
  }

  const removePending = (localId: string) => {
    uploads.current.get(localId)?.abort();
    setPending((all) => all.filter((p) => p.localId !== localId));
  };

  const hasFiles = (e: DragEvent) => [...e.dataTransfer.types].includes('Files');
  const dropProps = {
    onDragOver: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setDragging(true);
    },
    onDragLeave: (e: DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDragging(false);
      addFiles(e.dataTransfer.files);
    },
  };
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.clipboardData.files.length === 0) return;
    // Pasted screenshots and copied files are attached; pasted text stays text.
    if (!e.clipboardData.getData('text')) e.preventDefault();
    addFiles(e.clipboardData.files);
  };
  // Grow the composer with its text, up to a limit.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // A chat mounted while hidden measures 0; keep at least one comfortable line.
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 36), 192)}px`;
  }, [input]);

  // The composer moves from the centre to the bottom after the first message; keep typing there.
  const started = turns.length > 0;
  useEffect(() => {
    if (started) inputRef.current?.focus();
  }, [started]);

  const update = (id: number, fn: (turn: Turn) => Turn) =>
    setTurns((all) => all.map((t) => (t.id === id ? fn(t) : t)));

  const uploading = pending.some((p) => p.status === 'uploading');
  const failedUpload = pending.some((p) => p.status === 'error');
  const ready = pending.flatMap((p) => (p.attachment ? [p.attachment] : []));
  const canSend =
    (input.trim().length > 0 || ready.length > 0) &&
    !uploading &&
    !failedUpload &&
    requestContext !== null;

  async function send() {
    const content = input.trim();
    if (!canSend || running || requestContext === null) return;
    const attachments = ready;
    const userTurn: Turn = {
      id: nextId.current++,
      role: 'user',
      content,
      ...(attachments.length > 0 ? { attachments } : {}),
    };
    const replyId = nextId.current++;
    const message: ChatMessage = {
      role: 'user',
      content,
      ...(attachments.length > 0 ? { attachments } : {}),
    };
    const messages = stateful ? [message] : [...history(turns), message];
    setPending([]);
    setTurns((all) => [
      ...all,
      userTurn,
      {
        id: replyId,
        role: 'assistant',
        content: '',
        run: { runId: null, version: null, status: 'streaming' },
      },
    ]);
    setInput('');
    setRunning(true);
    const abort = new AbortController();
    abortRef.current = abort;
    try {
      await postEventStream(
        `/agents/${agentId}/playground`,
        {
          version,
          messages,
          // Always sent: the conversation is a thread in Traces (memory uses it only when stateful).
          threadId,
          ...(Object.keys(requestContext).length > 0 ? { requestContext } : {}),
          ...(headers ? { headers } : {}),
        },
        (event) => {
          if (event.type === 'run.started') {
            update(replyId, (t) => ({
              ...t,
              run: {
                ...t.run!,
                runId: event.runId,
                version: event.version,
                ...(event.sessions ? { sessions: event.sessions } : {}),
              },
            }));
          } else if (event.type === 'tool.call') {
            update(replyId, (t) => ({
              ...t,
              run: {
                ...t.run!,
                tools: [
                  ...(t.run!.tools ?? []),
                  { id: event.id, name: event.name, args: event.args },
                ],
              },
            }));
          } else if (event.type === 'tool.result') {
            update(replyId, (t) => ({
              ...t,
              run: {
                ...t.run!,
                tools: (t.run!.tools ?? []).map((x) =>
                  x.id === event.id ? { ...x, ok: event.ok } : x,
                ),
              },
            }));
          } else if (event.type === 'text.delta') {
            update(replyId, (t) => ({ ...t, content: t.content + event.text }));
          } else if (event.type === 'run.completed') {
            update(replyId, (t) => ({
              ...t,
              run: {
                ...t.run!,
                status: 'succeeded',
                latencyMs: event.latencyMs,
                usage: event.usage,
              },
            }));
          } else if (event.type === 'run.error') {
            update(replyId, (t) => ({
              ...t,
              run: {
                ...t.run!,
                status: 'failed',
                error: { code: event.code, message: event.message },
              },
            }));
          }
        },
        abort.signal,
      );
      if (abort.signal.aborted) {
        update(replyId, (t) => ({
          ...t,
          run: { ...t.run!, status: 'failed', error: { code: 'cancelled', message: 'Stopped' } },
        }));
      }
    } catch (error) {
      update(replyId, (t) => ({
        ...t,
        run: {
          ...t.run!,
          status: 'failed',
          error: { code: 'request', message: errorMessage(error) },
        },
      }));
    } finally {
      // A stream that ended without a final event (e.g. connection dropped) counts as failed.
      update(replyId, (t) =>
        t.run?.status === 'streaming'
          ? {
              ...t,
              run: {
                ...t.run,
                status: 'failed',
                error: { code: 'internal', message: 'The stream ended unexpectedly' },
              },
            }
          : t,
      );
      setRunning(false);
      abortRef.current = null;
    }
  }

  const { sendWith } = usePreferences();
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const mod = e.metaKey || e.ctrlKey;
    if (sendWith === 'enter' ? !mod : mod) {
      e.preventDefault();
      void send();
    }
  };

  const empty = turns.length === 0;
  const composer = (
    <form
      className="bg-border/80 focus-within:bg-ring/60 w-full rounded-[27px] p-px transition-colors"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <div className="bg-card rounded-[26px]">
        <PendingAttachments items={pending} onRemove={removePending} />
        <label htmlFor="playground-input" className="sr-only">
          Message
        </label>
        {/* One row: attach · text · actions; it grows upward with the text. */}
        <div className="flex items-end gap-1 p-2">
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            className="hidden"
            aria-label="Attach files"
            data-testid="attach-input"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-lg"
            aria-label="Attach files or images"
            className="text-muted-foreground hover:text-foreground rounded-full"
            title="Attach files or images (or drop / paste them)"
            onClick={() => fileRef.current?.click()}
            disabled={pending.length >= MAX_ATTACHMENTS_PER_MESSAGE}
          >
            <Paperclip aria-hidden />
          </Button>
          <textarea
            id="playground-input"
            ref={inputRef}
            rows={1}
            placeholder="Enter your message…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            className="placeholder:text-muted-foreground block max-h-48 min-w-0 flex-1 resize-none bg-transparent px-1 py-2 text-sm leading-5 outline-none"
          />
          {toolbar && <div className="flex shrink-0 items-center gap-1 self-center">{toolbar}</div>}
          {!empty && (
            <Button
              type="button"
              variant="ghost"
              size="icon-lg"
              aria-label="New chat"
              className="text-muted-foreground hover:text-foreground rounded-full"
              title="New chat"
              onClick={() => {
                setTurns([]);
                setThreadId(randomId());
                for (const c of uploads.current.values()) c.abort();
                setPending([]);
              }}
              disabled={running}
            >
              <RotateCcw aria-hidden />
            </Button>
          )}
          {running ? (
            <Button
              type="button"
              size="icon-lg"
              variant="outline"
              className="rounded-full"
              aria-label="Stop"
              onClick={() => abortRef.current?.abort()}
            >
              <Square className="size-3 fill-current" aria-hidden />
            </Button>
          ) : (
            <Button
              type="submit"
              size="icon-lg"
              className="rounded-full"
              aria-label="Send"
              title={
                uploading
                  ? 'Waiting for uploads'
                  : failedUpload
                    ? 'Remove the failed file'
                    : undefined
              }
              disabled={!canSend}
            >
              <ArrowUp aria-hidden />
            </Button>
          )}
        </div>
      </div>
    </form>
  );

  return (
    <div className={cn('relative flex min-h-0 flex-col', className)} {...dropProps}>
      {dragging && (
        <div
          className="bg-background/80 border-ring pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed text-sm backdrop-blur-sm"
          aria-hidden
        >
          <Upload className="size-6" />
          Drop files or images to attach
        </div>
      )}
      <div
        className={cn('min-h-0 flex-1 overflow-y-auto px-4', empty && 'flex flex-col')}
        role="log"
        aria-label="Conversation"
        aria-live="polite"
      >
        {empty ? (
          <div className="m-auto grid w-full max-w-3xl justify-items-center gap-5 py-10">
            <div className="bg-muted text-muted-foreground flex size-10 items-center justify-center rounded-full border text-sm font-medium">
              {agentName.slice(0, 1).toUpperCase()}
            </div>
            <p className="text-muted-foreground text-center text-xl">
              What can <span className="text-foreground font-medium">{agentName}</span> do for you
              today?
            </p>
            {composer}
            {/* Each phrase stays whole; narrow screens wrap at the dot. */}
            <p className="text-muted-foreground flex flex-wrap justify-center gap-x-1.5 text-center text-xs">
              <span>
                {sendWith === 'enter'
                  ? 'Enter to send, Shift+Enter for a new line'
                  : '⌘/Ctrl+Enter to send, Enter for a new line'}
              </span>
              <span aria-hidden>·</span>
              <span>Drop or paste files to attach</span>
            </p>
          </div>
        ) : (
          <div className="mx-auto grid w-full max-w-3xl gap-6 py-6">
            {turns.map((t) =>
              t.role === 'user' ? (
                <div
                  key={t.id}
                  className="grid max-w-[85%] justify-items-end gap-2 justify-self-end"
                >
                  {t.attachments && <MessageAttachments attachments={t.attachments} />}
                  {t.content && (
                    <div className="bg-accent rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap">
                      {t.content}
                    </div>
                  )}
                </div>
              ) : (
                <div key={t.id} className="grid gap-2" data-testid="assistant-reply">
                  {t.run?.tools && t.run.tools.length > 0 && <ToolCalls tools={t.run.tools} />}
                  {t.content ? (
                    <Markdown>{t.content}</Markdown>
                  ) : (
                    t.run?.status === 'streaming' && (
                      <span className="text-muted-foreground inline-flex items-center gap-2 text-sm">
                        <LoaderCircle className="size-3.5 animate-spin" aria-hidden />
                        Thinking…
                      </span>
                    )
                  )}
                  {t.run?.error && (
                    <p className="text-destructive text-sm" role="alert">
                      {t.run.error.message}
                    </p>
                  )}
                  <div className="text-muted-foreground text-xs">
                    {t.run && <RunMeta run={t.run} />}
                  </div>
                </div>
              ),
            )}
            <div ref={endRef} />
          </div>
        )}
      </div>
      {!empty && <div className="mx-auto w-full max-w-3xl px-4 pt-2 pb-4">{composer}</div>}
    </div>
  );
}
